import { Utils } from "../../tools/utils";

export const CLOUD_MAX_FILE_SIZE = (1 << 20) * 200;

export async function checkForExtension(selectedFile: File, extensions: string | string[]): Promise<boolean> {
    if (typeof (extensions) === 'string')
        extensions = extensions.split(',');

    for (let ext of extensions) {
        switch (ext.toLowerCase()) {
            case 'pdf':
                {
                    /* https://stackoverflow.com/a/79405432/9399492
                        On vérifie que le header existe dans les 1024 premiers octets
                        Le header est 25 50 44 46 2D
                    */

                    // Lecture des 1024 premiers octets
                    const firstBytes = new Uint8Array(await selectedFile.slice(0, 1024).arrayBuffer());

                    // Magic du header de 4 octets
                    const headerContent = [0x25, 0x50, 0x44, 0x46, 0x2D];

                    for (let i = 0; i < firstBytes.length - headerContent.length; i++) {
                        // Pour chaque itération, si le Magic correspond, l'extension est validée

                        let j = 0;
                        while (j < headerContent.length && firstBytes[i + j] === headerContent[j])
                            j++;

                        if (j === headerContent.length) {
                            return true;
                        }
                    }

                    break;
                }
            case 'zip':
                {
                    /* https://www.alchemistowl.org/pocorgtfo/pocorgtfo07.pdf, pages 20 & 29
                        "ZIP doesn’t require magic at offset zero, [...] it's parsed from the bottom up"

                        Plutôt que de vérifier le header, on vérifie le footer EOCD.
                        Sur les 0xFFFFF derniers octets, on cherche de bas en haut les octets 50 4B 05 06, qui 16 octets plus tard doit contenir une taille correspondant à moins du reste du fichier
                    */

                    // Lecture des 0xFFFFF derniers octets
                    const lastBytes = new Uint8Array(await selectedFile.slice(-Math.min(0xFFFFF, selectedFile.size)).arrayBuffer());

                    // Magic du footer de 4 octets
                    const footerContent = [0x50, 0x4B, 0x05, 0x06];

                    for (let i = lastBytes.length - footerContent.length - 18; i >= 0; i--) {
                        // Pour chaque itération, si le Magic ne correspond pas, ce n'est pas bon

                        let j = 0;
                        while (j < footerContent.length && lastBytes[i + j] === footerContent[j])
                            j++;

                        if (j !== footerContent.length)
                            continue;

                        // Si le magic correspond, on s'assure que la taille du comment ne dépasse pas la longueur du fichier
                        const commentSize = lastBytes[i + 20] | (lastBytes[i + 21] << 8);
                        if (lastBytes.length >= (i + 22 + commentSize)) {
                            return true;
                        }
                    }

                    break;
                }
            case 'xlsx':
                {
                    /* https://en.wikipedia.org/wiki/ZIP_(file_format)#Central_directory_file_header_(CDFH)
                        Un fichier XLSX, ce n'est ni plus ni moins qu'un ZIP.
                        On vérifie donc que c'est un ZIP valide, et on vérifie ensuite l'existence d'une entrée "xl/"

                        Pour récupérer les entrées, on récupère l'adresse du CDFH depuis l'EOCD détecté (Cf. explication précédente)
                        MAIS, il se peut qu'on soit sur un ZIP64, auquel cas l'EOCD nous donnera un offset de 0xFFFFFFFF !
                        Si c'est le cas, il faut trouver l'EOCD64, commençant par 50 4B 06 06, qui a des pointeurs de 8 octets de long, dont un remontant au CDFH.
                        Si on est en ZIP64, il se peut aussi qu'il y ait un chunk supplémentaire à la fin de chaque entrée du CDFH.

                        De là, on itère à travers chaque entrée du CDFH jusqu'à ce qu'on en trouve une qui a pour nom "xl/"
                    */

                    // Lecture des 0xFFFFF derniers octets
                    const lastBytes = new Uint8Array(await selectedFile.slice(-Math.min(0xFFFFF, selectedFile.size)).arrayBuffer());

                    // Magic du footer EOCD de 4 octets
                    const footerContent = [0x50, 0x4B, 0x05, 0x06];

                    // Magic du footer EOCD64 de 4 octets
                    const footer64Content = [0x50, 0x4B, 0x06, 0x06];

                    // Magic du bloc CDFH de 4 octets
                    const cdfhMagic = [0x50, 0x4B, 0x01, 0x02];

                    let footerOffs = -1;
                    for (let i = lastBytes.length - footerContent.length - 18; i >= 0; i--) {
                        // Pour chaque itération, si le Magic ne correspond pas, ce n'est pas bon

                        let j = 0;
                        while (j < footerContent.length && lastBytes[i + j] === footerContent[j])
                            j++;

                        if (j !== footerContent.length)
                            continue;

                        // Si la taille du comment dépasse la longueur du fichier, ce n'est pas bon
                        const commentSize = lastBytes[i + 20] | (lastBytes[i + 21] << 8);
                        if (lastBytes.length < (i + 22 + commentSize))
                            continue;

                        footerOffs = i;
                        break;
                    }

                    if (footerOffs < 0)
                        break;

                    // On récupère l'offset du premier bloc CDFH, ainsi que sa taille et son nombre d'entrées. 
                    let countCDFH = lastBytes[footerOffs + 10] | (lastBytes[footerOffs + 11] << 8);
                    let sizeCDFH = (lastBytes[footerOffs + 12] | (lastBytes[footerOffs + 13] << 8) | (lastBytes[footerOffs + 14] << 16) | (lastBytes[footerOffs + 15] << 24)) >>> 0;
                    let offsetCDFH = (lastBytes[footerOffs + 16] | (lastBytes[footerOffs + 17] << 8) | (lastBytes[footerOffs + 18] << 16) | (lastBytes[footerOffs + 19] << 24)) >>> 0;

                    // S'ils sont à 0xFFFFFFFF, c'est un ZIP64
                    let isZIP64 = false;
                    if (countCDFH === 0xFFFF || sizeCDFH === 0xFFFFFFFF || offsetCDFH === 0xFFFFFFFF) {
                        for (let i = lastBytes.length - footerContent.length - 18; i >= 0; i--) {
                            // Pour chaque itération, si le Magic ne correspond pas, ce n'est pas bon

                            let j = 0;
                            while (j < footer64Content.length && lastBytes[i + j] === footer64Content[j])
                                j++;

                            if (j !== footer64Content.length)
                                continue;

                            // Si la taille du EOCD64 dépasse la longueur du fichier, ce n'est pas bon
                            const eocd64Size = BigInt('0x'
                                + lastBytes[i + 11].toString(16).padStart(2, '0')
                                + lastBytes[i + 10].toString(16).padStart(2, '0')
                                + lastBytes[i + 9].toString(16).padStart(2, '0')
                                + lastBytes[i + 8].toString(16).padStart(2, '0')
                                + lastBytes[i + 7].toString(16).padStart(2, '0')
                                + lastBytes[i + 6].toString(16).padStart(2, '0')
                                + lastBytes[i + 5].toString(16).padStart(2, '0')
                                + lastBytes[i + 4].toString(16).padStart(2, '0')
                            );
                            if (lastBytes.length < (BigInt(i + 12) + eocd64Size))
                                continue;

                            countCDFH = Number(BigInt('0x'
                                + lastBytes[i + 39].toString(16).padStart(2, '0')
                                + lastBytes[i + 38].toString(16).padStart(2, '0')
                                + lastBytes[i + 37].toString(16).padStart(2, '0')
                                + lastBytes[i + 36].toString(16).padStart(2, '0')
                                + lastBytes[i + 35].toString(16).padStart(2, '0')
                                + lastBytes[i + 34].toString(16).padStart(2, '0')
                                + lastBytes[i + 33].toString(16).padStart(2, '0')
                                + lastBytes[i + 32].toString(16).padStart(2, '0')
                            ));

                            sizeCDFH = Number(BigInt('0x'
                                + lastBytes[i + 47].toString(16).padStart(2, '0')
                                + lastBytes[i + 46].toString(16).padStart(2, '0')
                                + lastBytes[i + 45].toString(16).padStart(2, '0')
                                + lastBytes[i + 44].toString(16).padStart(2, '0')
                                + lastBytes[i + 43].toString(16).padStart(2, '0')
                                + lastBytes[i + 42].toString(16).padStart(2, '0')
                                + lastBytes[i + 41].toString(16).padStart(2, '0')
                                + lastBytes[i + 40].toString(16).padStart(2, '0')
                            ));

                            offsetCDFH = Number(BigInt('0x'
                                + lastBytes[i + 55].toString(16).padStart(2, '0')
                                + lastBytes[i + 54].toString(16).padStart(2, '0')
                                + lastBytes[i + 53].toString(16).padStart(2, '0')
                                + lastBytes[i + 52].toString(16).padStart(2, '0')
                                + lastBytes[i + 51].toString(16).padStart(2, '0')
                                + lastBytes[i + 50].toString(16).padStart(2, '0')
                                + lastBytes[i + 49].toString(16).padStart(2, '0')
                                + lastBytes[i + 48].toString(16).padStart(2, '0')
                            ));

                            isZIP64 = true;

                            break;
                        }
                    }

                    // S'il est supérieur au footer, c'est que le fichier est invalide
                    if (offsetCDFH + sizeCDFH > selectedFile.size - lastBytes.length + footerOffs)
                        return false;

                    // On récupère les blocs CDFH, et on s'assure qu'on a bien une entrée égale à "xl/"
                    const cdfhBytes = new Uint8Array(await selectedFile.slice(offsetCDFH, offsetCDFH + sizeCDFH).arrayBuffer());
                    let k = 0;
                    let entryC = 0;
                    while (k < sizeCDFH && entryC < countCDFH) {
                        let l = 0;
                        while (l < cdfhMagic.length && cdfhBytes[k + l] === cdfhMagic[l])
                            l++;

                        if (l !== cdfhMagic.length)
                            break;

                        const filenameLength = cdfhBytes[k + 28] | (cdfhBytes[k + 29] << 8);
                        const extraLength = cdfhBytes[k + 30] | (cdfhBytes[k + 31] << 8);
                        const commentLength = cdfhBytes[k + 32] | (cdfhBytes[k + 33] << 8);

                        let decoded = new TextDecoder("utf-8").decode(cdfhBytes.slice(k + 46, k + 46 + filenameLength));

                        if (decoded.startsWith('xl/')) {
                            return true;
                        }

                        const postChunkBase = 46 + filenameLength + extraLength + commentLength;
                        if (isZIP64) {
                            const compressedSize = (cdfhBytes[k + 20] | (cdfhBytes[k + 21] << 8) | (cdfhBytes[k + 22] << 16) | (cdfhBytes[k + 23] << 24)) >>> 0;
                            const uncompressedSize = (cdfhBytes[k + 24] | (cdfhBytes[k + 25] << 8) | (cdfhBytes[k + 26] << 16) | (cdfhBytes[k + 27] << 24)) >>> 0;
                            const diskNumber = cdfhBytes[k + 34] | (cdfhBytes[k + 35] << 8);
                            const lhrOffs = (cdfhBytes[k + 42] | (cdfhBytes[k + 43] << 8) | (cdfhBytes[k + 44] << 16) | (cdfhBytes[k + 45] << 24)) >>> 0;

                            if ((compressedSize === 0xFFFFFFFF || uncompressedSize === 0xFFFFFFFF || diskNumber === 0xFFFF || lhrOffs === 0xFFFFFFFF) && cdfhBytes[k + postChunkBase] === 1 && cdfhBytes[k + postChunkBase + 1] === 0) {
                                k += cdfhBytes[k + postChunkBase + 2] | (cdfhBytes[k + postChunkBase + 3] << 8);
                            }
                        }

                        k += postChunkBase;
                        entryC++;
                    }

                    break;
                }
            case 'xls':
                {
                    /* https://en.wikipedia.org/wiki/Compound_File_Binary_Format & https://winprotocoldoc.z19.web.core.windows.net/MS-CFB/%5bMS-CFB%5d.pdf
                        Un fichier XLS est un fichier CFBF
                        On vérifie donc que c'est un CFBF valide, et on vérifie ensuite l'existence d'une entrée "workbook"

                        Le standard n'étant pas clair sur le formattage des DIFAT, j'ai dû analyser des fichiers existants.
                        Il semblerait que le champ de taille de DIFAT ajoute des secteurs EN PLUS des 109 contenus dans le header.
                        S'il y a plusieurs de ces secteurs, ils seront bout-à-bout, puisque les FAT donnent la valeur spéciale FC FF FF FF sur les secteurs DIFAT

                        Fichiers de référence:
                        - 2 DIFAT sectors: "L:\Elec_dev\Projets Windev\Outils Color Director\BDD Surfaces\source\2020-11-17\ExportSerie.xls"
                        - 1 DIFAT sector: "L:\Elec_dev\Projets Windev\xml tarifs\2025\AD\2025-02-17\3-LACOUR 17 FEVRIER mod.xls"
                        - 0 DIFAT sectors: "L:\Elec_Documentation\INFORMATIQUE\Documents\Programmation\Traduction\Traduction IXELL origine.xls"

                        Avec le mappage des DIFAT, on obtient les FAT qui permettent d'enchaîner les secteurs Directory, qui contiennent les entrées des fichiers
                        Dans l'idée, un XLS ne contient de toute façon que 4 entrées, qui tiennent donc pile poil en un secteur Directory, épargant en théorie l'usage des DIFAT.
                        Dans le doute, on vérifie le premier secteur Directory, et s'il ne contient pas d'entrée "workbook", alors on parse les DIFAT et les FAT pour voir s'il n'y en aurais pas d'autres.
                        Si le CFBF est en version 4, on peut potentiellement s'épargner cette vérification en lisant le champ "Number of Directory Sectors"

                        C'est quand même incroyable d'avoir voulu stocker les fichiers Office 97-2003 dans un format aussi compliqué, tout ça pour n'y mettre que 4 entrées dont la principa n'est ni plus ni moins qu'un ZIP...
                        Visiblement, ils ont compris à quel point c'était bête puisqu'ils sont par la suite passé au XLSX/WORDX/PPTX/etc pour Office 2007+, mais ça reste bête...
                    */

                    // Lecture des 512 premiers octets
                    const firstBytes = new Uint8Array(await selectedFile.slice(0, 512).arrayBuffer());

                    // Magic du header de 8 octets
                    const headerContent = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];

                    // Si le Magic ne correspond pas, ce n'est pas bon
                    let magicI = 0;
                    while (magicI < headerContent.length && firstBytes[magicI] === headerContent[magicI])
                        magicI++;

                    if (magicI !== headerContent.length)
                        break;

                    // On prend le SECT initial des entrées Directory, puis avec la DIFAT, on récupère les FAT qui nous permettent de calculer l'enchaînement de Directory.
                    const sectorSize = 1 << (firstBytes[0x1E] | (firstBytes[0X1F] << 8));
                    const firstDirSect = (firstBytes[0x30] | (firstBytes[0x31] << 8) | (firstBytes[0x32] << 16) | (firstBytes[0x33] << 24)) >>> 0;

                    const checkSector = async (sectorNum: number): Promise<boolean> => {
                        const sectorPtr = (sectorNum + 1) * sectorSize;
                        const sectorBytes = new Uint8Array(await selectedFile.slice(sectorPtr, sectorPtr + sectorSize).arrayBuffer());
                        for (let entryI = 0; entryI < sectorBytes.length; entryI += 0x80) {
                            let charI = 0;
                            while (charI < 0x40 && (sectorBytes[entryI + charI] || sectorBytes[entryI + charI + 1])) {
                                charI += 2;
                            }

                            let decoded = new TextDecoder("utf-16le").decode(sectorBytes.slice(entryI, entryI + charI));

                            if (decoded === 'Workbook')
                                return true;
                        }

                        return false;
                    }

                    if (await checkSector(firstDirSect))
                        return true;

                    // TODO: check other sectors

                    break;
                }
            case 'png':
                {
                    /* https://en.wikipedia.org/wiki/PNG#File_format
                        Un fichier PNG dispose d'un en-tête, puis est décomposés en chunks.
                        Pour une vérification basique, on vérifie l'en-tête, que chaque chunk a la bonne checksum, que le premier est de type IDHR, et que le dernier est de type IEND.

                        Une vérification beaucoup plus poussée existe à https://github.com/nayuki/Nayuki-web-published-code/blob/master/png-file-chunk-inspector/png-file-chunk-inspector.ts
                        Mais il faut contacter l'auteur pour obtenir la license.

                        Vérification plus poussée à explorer sur le long terme !
                        Ma grosse crainte, c'est que quelqu'un fasse un fichier PNG avec le bon en-tête et le bon chunk de fin, mais avec n'importe quoi au milieu, et que ça fasse planter le logiciel qui veut ensuite afficher l'image.
                    */

                    // Lecture des 8 premiers octets
                    const firstBytes = new Uint8Array(await selectedFile.slice(0, 8).arrayBuffer());

                    // Magic du header de 8 octets
                    const headerContent = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];

                    // Si le Magic ne correspond pas, ce n'est pas bon
                    let magicI = 0;
                    while (magicI < headerContent.length && firstBytes[magicI] === headerContent[magicI])
                        magicI++;

                    if (magicI !== headerContent.length)
                        break;

                    let offset = 8;
                    let chunkI = 0;
                    while (offset < selectedFile.size) {
                        // Lecture des 8 premiers octets du chunk
                        const chunkInfo = new Uint8Array(await selectedFile.slice(offset, offset + 4).arrayBuffer());

                        const chunkLength = ((chunkInfo[0] << 24) | (chunkInfo[1] << 16) | (chunkInfo[2] << 8) | chunkInfo[3]) >>> 0;

                        offset += 4;

                        // Lecture du type + des données du chunk
                        const chunkData = new Uint8Array(await selectedFile.slice(offset, offset + 4 + chunkLength).arrayBuffer());

                        const chunkType = String.fromCharCode(chunkData[0], chunkData[1], chunkData[2], chunkData[3]);

                        offset += 4 + chunkLength;

                        // Lecture des 4 derniers octets du chunk
                        const chunkChecksumBytes = new Uint8Array(await selectedFile.slice(offset, offset + 4).arrayBuffer());

                        const chunkChecksum = ((chunkChecksumBytes[0] << 24) | (chunkChecksumBytes[1] << 16) | (chunkChecksumBytes[2] << 8) | chunkChecksumBytes[3]) >>> 0;

                        offset += 4;

                        const computedChecksum = Utils.crc32(chunkData);

                        // Si mauvaise checksum, le fichier n'est pas bon
                        if (computedChecksum !== chunkChecksum)
                            return false;

                        // Si IHDR n'est pas le premier chunk ou n'est pas unique, le fichier n'est pas bon
                        if ((chunkType === 'IHDR') !== (chunkI === 0))
                            return false;

                        // Si IEND n'est pas le dernier chunk ou n'est pas unique, le fichier n'est pas bon
                        if ((chunkType === 'IEND') !== (offset === selectedFile.size))
                            return false;

                        chunkI++;
                    }

                    return true;
                }
            case "ppt":
            case "pptx":
                return true;
            default:
                throw new Error('Unsupported extension');
        }
    }

    return false;
}

// https://stackoverflow.com/a/57804808/9399492
export function downloadBase64File(contentBase64: string, fileName: string) {
    console.log('Downloading ' + fileName);

    const linkSource = `data:${fileName.toLocaleLowerCase().endsWith('.pdf') ? 'application/pdf' : fileName.toLocaleLowerCase().endsWith('.png') ? 'image/png' : 'application/octet-stream'};base64,${contentBase64}`;
    const downloadLink = document.createElement('a');
    document.body.appendChild(downloadLink);

    downloadLink.href = linkSource;
    downloadLink.target = '_self';
    downloadLink.download = fileName;
    downloadLink.click();

    downloadLink.remove();
}

export const MIN_COUNTRY_MODE = 2;
